import React from 'react';
import { Check, Download } from 'lucide-react';
import type { OrderItem } from '../types/products';
import type { Visionary } from '../types/visionary';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../lib/supabase';

export interface OrderConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  orderItems: OrderItem[];
  orderId: string;
  staffName?: string;
  staffId?: number;
  visionary: Visionary | null;
  pointsUsed: number;
  pointsEarned: number;
  total: number;
}

export default function OrderConfirmation({
  isOpen,
  onClose,
  orderItems,
  orderId,
  staffName,
  staffId,
  visionary,
  pointsUsed,
  pointsEarned,
  total
}: OrderConfirmationProps) {
  const [currentVisionaryData, setCurrentVisionaryData] = React.useState<Visionary | null>(visionary);

  // Function to refresh visionary data from database - EXACT same as Account.tsx
  const refreshVisionaryData = async () => {
    if (!visionary?.membership_number) return;
    
    try {
      const { data, error } = await supabase
        .from('visionaries')
        .select('*')
        .eq('membership_number', visionary.membership_number)
        .single();
      
      if (error) {
        console.error('Error refreshing visionary data:', error);
        return;
      }
      
      if (data) {
        setCurrentVisionaryData(data);
      }
    } catch (error) {
      console.error('Failed to refresh visionary data:', error);
    }
  };

  // Fetch live data when modal opens - EXACT same as Account.tsx
  React.useEffect(() => {
    if (isOpen && visionary) {
      refreshVisionaryData();
    }
  }, [isOpen, visionary]);

  if (!isOpen) return null;

  const generateInvoice = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header with company details
    doc.setFontSize(28);
    doc.setTextColor(88, 28, 135); // Purple color for brand
    doc.text('Big Vision Games', pageWidth / 2, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128); // Gray for secondary text
    doc.text('Gaming Beyond Boundaries', pageWidth / 2, 32, { align: 'center' });
    
    // Divider line
    doc.setDrawColor(88, 28, 135);
    doc.setLineWidth(0.5);
    doc.line(20, 38, pageWidth - 20, 38);

    // Invoice details section
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text('Order Confirmation', 20, 50);

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    // Left column - Order details
    doc.text('Order Details:', 20, 65);
    doc.text(`Order Number: ${orderId}`, 20, 72);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 79);
    if (staffName) {
      doc.text(`Staff Member: ${staffName}`, 20, 86);
      doc.text(`Staff ID: ${staffId}`, 20, 93);
    }

    // Right column - Company details
    const rightColumn = pageWidth - 80;
    doc.text('Company Details:', rightColumn, 65);
    doc.text('Big Vision Games Ltd.', rightColumn, 72);
    doc.text('190 Cathedral Street', rightColumn, 79);
    doc.text('Glasgow, UK G4 0RF', rightColumn, 86);
    doc.text('Tel: 0141 321 1056', rightColumn, 93);

    // Customer section
    let yPos = 110;
    if (currentVisionaryData) {
      doc.setFontSize(12);
      doc.setTextColor(88, 28, 135);
      doc.text('Customer Information', 20, yPos);
      
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      yPos += 8;
      doc.text(`${currentVisionaryData.name}`, 20, yPos);
      yPos += 7;
      doc.text(`${currentVisionaryData.email}`, 20, yPos);
      yPos += 7;
      doc.text(`${currentVisionaryData.address}`, 20, yPos);
      yPos += 7;
      doc.text(`Visionary ID: ${currentVisionaryData.membership_number}`, 20, yPos);
      yPos += 7;
      doc.text(`Tel: ${currentVisionaryData.telephone}`, 20, yPos);
      yPos += 15;
    } else {
      yPos = 120;
    }

    // Items table with enhanced styling
    const tableData = orderItems.map(item => [
      item.product.id.toString(),
      `${item.product.name}${item.product.platform ? ` (${item.product.platform})` : ''}`,
      item.quantity.toString(),
      `£${item.product.price.toFixed(2)}`,
      `£${(item.product.price * item.quantity).toFixed(2)}`
    ]);

    // Add table with didDrawPage hook to handle footers on each page
    autoTable(doc, {
      startY: yPos,
      head: [['Product ID', 'Item Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 5,
      },
      headStyles: {
        fillColor: [88, 28, 135],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Product ID
        1: { cellWidth: 'auto' }, // Item Description
        2: { cellWidth: 20, halign: 'center' }, // Quantity
        3: { cellWidth: 30, halign: 'right' }, // Unit Price
        4: { cellWidth: 30, halign: 'right' }, // Total
      },
      margin: { bottom: 60 }, // Leave space for totals at bottom
      didDrawPage: (data) => {
        // Add page number at the bottom of each page
        const pageNumber = (doc.internal as any).getCurrentPageInfo().pageNumber;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${pageNumber}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 10
        );
      }
    });

    // Get the final Y position after the table (on whatever page it ended)
    const finalY = (doc as any).lastAutoTable.finalY + 15;
    const pageHeight = doc.internal.pageSize.height;

    // Check if we need to add a new page for totals
    if (finalY > pageHeight - 60) {
      doc.addPage();
    }

    // Add a subtle line above totals
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(pageWidth - 100, finalY - 5, pageWidth - 20, finalY - 5);

    // Points Information
    if (pointsUsed > 0 || pointsEarned > 0) {
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text('Points Summary:', pageWidth - 100, finalY);
      doc.text(`Points Used:`, pageWidth - 100, finalY + 7);
      doc.text(`${pointsUsed}`, pageWidth - 30, finalY + 7, { align: 'right' });
      doc.text(`Points Earned:`, pageWidth - 100, finalY + 14);
      doc.text(`${pointsEarned}`, pageWidth - 30, finalY + 14, { align: 'right' });
      doc.text(`Savings:`, pageWidth - 100, finalY + 21);
      doc.text(`£${(pointsUsed / 100).toFixed(2)}`, pageWidth - 30, finalY + 21, { align: 'right' });
      
      // Final total with bold styling
      doc.setFontSize(12);
      doc.setFont('Helvetica', 'bold');
      doc.text('Total Amount:', pageWidth - 100, finalY + 32);
      doc.text(`£${total.toFixed(2)}`, pageWidth - 30, finalY + 32, { align: 'right' });
      
      // Footer note
      if (currentVisionaryData) {
        doc.setFontSize(9);
        doc.setFont('Helvetica', 'normal');
        doc.setTextColor(128, 128, 128);
        doc.text(`A copy of this invoice has been emailed to ${currentVisionaryData.email}`, pageWidth / 2, finalY + 45, { align: 'center' });
      }
    } else {
      // Simple total for non-visionary customers
      doc.setFontSize(12);
      doc.setFont('Helvetica', 'bold');
      doc.text('Total Amount:', pageWidth - 100, finalY + 7);
      doc.text(`£${total.toFixed(2)}`, pageWidth - 30, finalY + 7, { align: 'right' });
    }

    // Footer on the last page
    const footerY = doc.internal.pageSize.height - 20;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Thank you for shopping with Big Vision Games', pageWidth / 2, footerY, { align: 'center' });

    doc.save(`BVG-Invoice-${orderId}.pdf`);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4">
            <Check className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Order Confirmed!</h2>
          <p className="text-white/70 mt-1">Order #{orderId}</p>
        </div>

        <div className="space-y-6">
          {/* Order Details */}
          <div className="space-y-4">
            {orderItems.map(({ product, quantity }) => (
              <div key={product.id} className="flex justify-between text-white">
                <span>
                  {product.name} × {quantity}
                  {product.platform && (
                    <span className="text-white/50 ml-1">({product.platform})</span>
                  )}
                </span>
                <span>£{(product.price * quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Points Information */}
          {currentVisionaryData && (
            <div className="pt-4 border-t border-white/10 space-y-2">
              <div className="flex justify-between text-white/70">
                <span>Points Used</span>
                <span>-{pointsUsed}</span>
              </div>
              <div className="flex justify-between text-white/70">
                <span>Points Earned</span>
                <span>+{pointsEarned}</span>
              </div>
            </div>
          )}

          {/* Staff Information */}
          {staffName && (
            <div className="pt-4 border-t border-white/10">
              <div className="text-white/70">
                Processed by: {staffName} (ID: {staffId})
              </div>
            </div>
          )}

          {/* Total */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex justify-between text-white font-semibold text-lg">
              <span>Total</span>
              <span>£{total.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={generateInvoice}
              className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Invoice
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}